import {
  Button, Checkbox, Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText, Grid, IconButton,
  Input,
  InputLabel,
  makeStyles, MenuItem, Paper, Select,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {TransitionProps} from '@material-ui/core/transitions/transition';
import React, {useEffect, useRef, useState} from 'react';
import * as yup from 'yup';
import {Formik, FormikProps} from 'formik';
import Cube, {DifficultyType, GameType, JourneyPhase, LengthOfGame, NumberOfPlayers} from '../../../Domain/Models/Cube';
import Loading from "../../UiComponents/Loading";
import GamePointsSettings, {PointsPerLife} from "../../../Domain/Models/GamePointsSettings";
import {cubeRepository} from "../../../Domain/Repositories/CubeRepository";
import {gameRepository} from "../../../Domain/Repositories/GameRepository";
import CubeHelp from "../../../Domain/Models/CubeHelp";
import {cubeHelpRepository} from "../../../Domain/Repositories/CubeHelpRepository";
import {KeyboardArrowDown, KeyboardArrowUp} from "@material-ui/icons";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  form: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  dialog: {
    alignItems: 'flex-start',
  },
  deleteFlex: {
    flex: 1,
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
}));

export interface CubeHelpModalProps
{
  open: boolean;
  onClose: () => void;
  cubeHelp: CubeHelp;
}

export default function CubeHelpModal(props: CubeHelpModalProps)
{
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {open, onClose, cubeHelp} = props;
  const classes = useStyles();

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [modifiedCubeHelp, setModifiedCubeHelp] = useState<CubeHelp>();

  const formRef = useRef<FormikProps<CubeHelp>>(null);

  const schema = yup.object().shape({
  });

  const handleSubmit = (value: CubeHelp) =>
  {
    setModifiedCubeHelp(value);
    setSubmitting(true);
    setSubmitError(undefined);

    cubeHelpRepository.update(value)
      .then(() => onClose())
      .catch((e: Error) =>
      {
        setSubmitting(false);
        setSubmitError(e.message);
      });
  }

  useEffect(() =>
  {
    if (open)
    {
      setModifiedCubeHelp(cubeHelp);
      setSubmitError(undefined);
      setSubmitting(false);
    }
  }, [open]);

  return (
      <Dialog
          classes={{scrollPaper: classes.dialog}}
          open={open}
          onClose={onClose}
          fullScreen={fullScreen}
          fullWidth
          maxWidth="md"
          disableEscapeKeyDown
          disableBackdropClick={submitting}
          TransitionComponent={Transition}
      >
        <DialogTitle>{`Cube Help - ${modifiedCubeHelp?.difficulty.toString()}`}</DialogTitle>

        <Loading isLoading={submitting}>

          <DialogContent>
            <Paper variant="outlined" className={classes.paper}>
          <Grid container alignItems="center" justify="center">
            <Grid item xs={6}>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                # This is a Heading
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                ## This is a Sub-Heading
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                _This text is in italics_
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                **This text is bold**
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                * This text bullet pointed
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography component="h1" variant="h5" color="textPrimary" align="center">
                This is a Heading
              </Typography>
              <Typography component="h2" variant="h6" color="textPrimary" align="center">
                This is a Sub-Heading
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                <i>This text is in italics</i>
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                <b>This text is bold</b>
              </Typography>
              <Typography component="p" variant="body1" color="textPrimary" align="center">
                <li>This text bullet pointed</li>
              </Typography>
            </Grid>
          </Grid>
            </Paper>
            
            <Formik<CubeHelp>
                initialValues={modifiedCubeHelp ?? cubeHelp}
                onSubmit={(value) => handleSubmit(value)}
                validationSchema={schema}
                innerRef={formRef}
            >
              {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur
                }) => (
                  <>
                    <FormControl fullWidth margin="normal" error={touched.rules && errors.rules !== undefined}>
                      <InputLabel htmlFor="component-error">Rules</InputLabel>
                      <Input
                        name="rules"
                        value={values.rules}
                        multiline={true}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.rules && errors.rules}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.additionalInfo && errors.additionalInfo !== undefined}>
                      <InputLabel htmlFor="component-error">Additional Info</InputLabel>
                      <Input
                        name="additionalInfo"
                        value={values.additionalInfo}
                        multiline={true}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.additionalInfo && errors.additionalInfo}</FormHelperText>
                    </FormControl>
                    
                    <FormControl fullWidth margin="normal" error={touched.resetInfo && errors.resetInfo !== undefined}>
                      <InputLabel htmlFor="component-error">Reset Info</InputLabel>
                      <Input
                        name="resetInfo"
                        value={values.resetInfo}
                        multiline={true}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.resetInfo && errors.resetInfo}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.simplifyInfo && errors.simplifyInfo !== undefined}>
                      <InputLabel htmlFor="component-error">Simplify Info</InputLabel>
                      <Input
                        name="simplifyInfo"
                        value={values.simplifyInfo}
                        multiline={true}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                      <FormHelperText>{touched.simplifyInfo && errors.simplifyInfo}</FormHelperText>
                    </FormControl>
                  </>
              )}
            </Formik>

            {submitError != undefined && (
                <Typography component="h3" variant="subtitle1" color="error" gutterBottom>
                  {submitError}
                </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button
                onClick={onClose} color="primary"
            >
              Cancel
            </Button>
            <Button
                onClick={() => formRef.current?.handleSubmit()}
                color="primary"
            >
              Submit
            </Button>
          </DialogActions>

        </Loading>
      </Dialog>
  );
}